import {
    AlertTriangle,
    ArrowRight,
    Calendar,
    ChevronLeft,
    Clock3,
    Info,
    LoaderCircle,
    Zap,
} from 'lucide-react'
import React, { useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './LeavePolicySetupScreen.module.css'

const LEAVE_TYPE_CONFIG = {
    CL: { label: 'Casual Leave', description: 'Used for flexible time off' },
    SL: { label: 'Sick Leave', description: 'For medical absences' },
    PL: { label: 'Privilege / Earned Leave', description: 'Annual earned entitlement' },
    CO: { label: 'Compensatory Off', description: 'Earned for working holidays' },
}

const FIELD_LABELS = {
    annualEntitlement: 'Annual Entitlement',
    accrualType: 'Accrual Type',
    accrualPerMonth: 'Monthly Accrual',
    carryForwardAllowed: 'Carry Forward',
    maxCarryForwardDays: 'Max Carry Forward',
    encashmentAllowed: 'Encashment',
    maxEncashmentDaysPerYear: 'Max Encashment/Year',
}

const POLICY_PRESETS = [
    {
        leaveType: 'CL',
        annualEntitlement: 12,
        accrualType: 'UPFRONT',
        accrualPerMonth: 0,
        carryForwardAllowed: false,
        maxCarryForwardDays: 0,
        encashmentAllowed: false,
        maxEncashmentDaysPerYear: 0,
    },
    {
        leaveType: 'SL',
        annualEntitlement: 6,
        accrualType: 'UPFRONT',
        accrualPerMonth: 0,
        carryForwardAllowed: false,
        maxCarryForwardDays: 0,
        encashmentAllowed: false,
        maxEncashmentDaysPerYear: 0,
    },
    {
        leaveType: 'PL',
        annualEntitlement: 15,
        accrualType: 'MONTHLY',
        accrualPerMonth: 1.25,
        carryForwardAllowed: true,
        maxCarryForwardDays: 30,
        encashmentAllowed: false,
        maxEncashmentDaysPerYear: 0,
    },
    {
        leaveType: 'CO',
        annualEntitlement: 0,
        accrualType: 'UPFRONT',
        accrualPerMonth: 0,
        carryForwardAllowed: false,
        maxCarryForwardDays: 0,
        encashmentAllowed: false,
        maxEncashmentDaysPerYear: 0,
    },
]

const getInitialPolicies = (preselectTypes = []) => {
    if (!Array.isArray(preselectTypes) || preselectTypes.length === 0) {
        return POLICY_PRESETS.filter((policy) => ['CL', 'SL', 'PL'].includes(policy.leaveType))
    }

    const selected = preselectTypes
        .map((type) => String(type || '').toUpperCase())
        .filter((type) => POLICY_PRESETS.some((policy) => policy.leaveType === type))

    if (selected.length === 0) {
        return POLICY_PRESETS.filter((policy) => ['CL', 'SL', 'PL'].includes(policy.leaveType))
    }

    return POLICY_PRESETS.filter((policy) => selected.includes(policy.leaveType))
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const LeavePolicySetupScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const preselectTypes = Array.isArray(location?.state?.preselectTypes)
        ? location.state.preselectTypes
        : []

    const [policies, setPolicies] = useState(() => getInitialPolicies(preselectTypes))
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)
    const [effectiveFrom, setEffectiveFrom] = useState(new Date().getFullYear() + '-01-01')

    const goBack = () => {
        navigate(-1)
    }

    const updatePolicy = (index, field, value) => {
        const updated = [...policies]
        updated[index] = { ...updated[index], [field]: value }
        setPolicies(updated)
        setErrors((prev) => {
            const next = { ...prev }
            delete next[`${field}_${index}`]
            return next
        })
    }

    const calculateTotalAnnualLeave = () => {
        return policies.reduce((sum, p) => sum + (p.annualEntitlement || 0), 0)
    }

    const createLeavePolicy = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            toast.error('Missing authentication or store information.')
            return
        }

        /* Client-side validation */
        const newErrors = {}
        policies.forEach((p, i) => {
            if (p.annualEntitlement < 0) {
                newErrors[`entitlement_${i}`] = `${LEAVE_TYPE_CONFIG[p.leaveType].label}: entitlement cannot be negative.`
            }
            if (p.accrualType === 'MONTHLY' && p.annualEntitlement > 0 && p.accrualPerMonth <= 0) {
                newErrors[`accrualPerMonth_${i}`] = `${LEAVE_TYPE_CONFIG[p.leaveType].label}: monthly accrual required for MONTHLY type.`
            }
            if (p.carryForwardAllowed && p.maxCarryForwardDays < 0) {
                newErrors[`maxCarryForward_${i}`] = `${LEAVE_TYPE_CONFIG[p.leaveType].label}: max carry-forward cannot be negative.`
            }
            if (p.encashmentAllowed && p.maxEncashmentDaysPerYear < 0) {
                newErrors[`maxEncashment_${i}`] = `${LEAVE_TYPE_CONFIG[p.leaveType].label}: max encashment cannot be negative.`
            }
        })

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            toast.error('Please fix validation errors before saving.')
            return
        }
        setErrors({})

        try {
            setIsSaving(true)

            const response = await fetch(BASE_URL_API + 'pay/createStoreLeavePolicy', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    effectiveFrom: effectiveFrom,
                    policies: policies.map((p) => ({
                        leaveType: p.leaveType,
                        annualEntitlement: p.annualEntitlement,
                        accrualType: p.accrualType,
                        accrualPerMonth: p.accrualPerMonth || 0,
                        carryForwardAllowed: p.carryForwardAllowed ? 1 : 0,
                        maxCarryForwardDays: p.maxCarryForwardDays || 0,
                        encashmentAllowed: p.encashmentAllowed ? 1 : 0,
                        maxEncashmentDaysPerYear: p.maxEncashmentDaysPerYear || 0,
                    })),
                }),
            })

            if (!response.ok) {
                toast.error('Network error. Please try again.')
                setIsSaving(false)
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const { totalCreated, totalSkipped, warnings } = respo.response

                warnings?.forEach((w) => {
                    if (w.type === 'HIGH_LEAVE_ENTITLEMENT') {
                        toast.warning(w.message)
                    }
                })

                if (totalSkipped > 0) {
                    toast.info(
                        `${totalCreated} policy type(s) created. ${totalSkipped} already existed — skipped.`,
                        { autoClose: 3000 }
                    )
                } else {
                    toast.success(`Leave policy configured for ${totalCreated} leave type(s).`)
                }

                navigate('/payroll/setup', { replace: true })
            } else {
                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                switch (respo?.errorCode) {
                    case 'CONF_001':
                        toast.info('All leave types already configured. Use the edit option to update.')
                        navigate('/payroll/setup', { replace: true })
                        break
                    case 'VAL_001':
                        toast.error(respo.message)
                        break
                    case 'VAL_003':
                        toast.error(respo.message)
                        break
                    case 'VAL_004':
                        toast.error('Duplicate leave types in your request.')
                        break
                    case 'VAL_005':
                        toast.error('Invalid effective date.')
                        break
                    case 'SYS_002':
                        toast.error('Database error. Please try again.')
                        break
                    default:
                        toast.error(respo?.message || 'Failed to save leave policy.')
                        break
                }
            }
        } catch (error) {
            console.error('createStoreLeavePolicy error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const totalLeave = calculateTotalAnnualLeave()
    const isHighEntitlement = totalLeave > 45

    return (
        <div className={styles.page}>
            {/* ── Header ── */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={goBack}>
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <div className={styles.heroPill}>
                        <Zap size={12} />
                        Setup
                    </div>
                    <h1 className={styles.headerTitle}>Leave Policy Setup</h1>
                    <p className={styles.headerSub}>Configure leave entitlements for your staff</p>
                </div>
            </div>

            {/* ── Form Wrap ── */}
            <div className={styles.formWrap}>
                {/* ── Effective From ── */}
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        <Calendar size={14} />
                        POLICY EFFECTIVE DATE
                    </div>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            Policy Effective From <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="date"
                            className={styles.textInput}
                            value={effectiveFrom}
                            onChange={(e) => setEffectiveFrom(e.target.value)}
                        />
                        <p className={styles.fieldHint}>
                            Leave balance will be reset from this date. Typically January 1st of the financial year.
                        </p>
                    </div>
                </div>

                {/* ── Leave Type Cards ── */}
                {policies.map((policy, idx) => (
                    <LeaveTypeCard
                        key={policy.leaveType}
                        policy={policy}
                        index={idx}
                        onUpdate={updatePolicy}
                        errors={errors}
                    />
                ))}

                {/* ── Summary Row ── */}
                <div className={`${styles.summaryCard} ${isHighEntitlement ? styles.summaryHighEntitlement : ''}`}>
                    <div className={styles.summaryContent}>
                        <span className={styles.summaryLabel}>Total Annual Leave</span>
                        <span className={styles.summaryValue}>{totalLeave.toFixed(1)} days</span>
                    </div>
                    {isHighEntitlement && (
                        <div className={styles.summaryWarning}>
                            <AlertTriangle size={14} />
                            <span>Exceeds 45 days — verify before finalizing</span>
                        </div>
                    )}
                </div>

                {/* ── Save Button ── */}
                <button
                    className={styles.saveBtn}
                    onClick={createLeavePolicy}
                    disabled={isSaving}
                    type="button"
                >
                    {isSaving ? (
                        <>
                            <LoaderCircle size={16} className={styles.spinIcon} />
                            Saving...
                        </>
                    ) : (
                        <>
                            Save Leave Policy
                            <ArrowRight size={16} />
                        </>
                    )}
                </button>
            </div>
        </div>
    )
}

/* ── Leave Type Card Component ── */
const LeaveTypeCard = ({ policy, index, onUpdate, errors }) => {
    const config = LEAVE_TYPE_CONFIG[policy.leaveType]

    return (
        <div className={styles.sectionCard}>
            <div className={styles.cardHeader}>
                <div>
                    <h3 className={styles.cardTitle}>{config.label}</h3>
                    <p className={styles.cardDesc}>{config.description}</p>
                </div>
            </div>

            {/* Annual Entitlement */}
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                    Annual Entitlement <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                    <input
                        type="number"
                        className={`${styles.textInput} ${errors[`entitlement_${index}`] ? styles.inputError : ''}`}
                        value={policy.annualEntitlement}
                        onChange={(e) => onUpdate(index, 'annualEntitlement', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.5"
                    />
                    <span className={styles.inputUnit}>days/year</span>
                </div>
                {errors[`entitlement_${index}`] && (
                    <p className={styles.fieldError}>{errors[`entitlement_${index}`]}</p>
                )}
            </div>

            {/* Accrual Type */}
            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Accrual Type</label>
                <div className={styles.segmentBtnGroup}>
                    <button
                        className={`${styles.segmentBtn} ${
                            policy.accrualType === 'UPFRONT' ? styles.segmentBtnActive : ''
                        }`}
                        onClick={() => onUpdate(index, 'accrualType', 'UPFRONT')}
                        type="button"
                    >
                        <Clock3 size={13} />
                        Upfront
                    </button>
                    <button
                        className={`${styles.segmentBtn} ${
                            policy.accrualType === 'MONTHLY' ? styles.segmentBtnActive : ''
                        }`}
                        onClick={() => onUpdate(index, 'accrualType', 'MONTHLY')}
                        type="button"
                    >
                        <Zap size={13} />
                        Monthly
                    </button>
                </div>
                <p className={styles.fieldHint}>
                    {policy.accrualType === 'UPFRONT'
                        ? 'Full entitlement credited on effective date.'
                        : 'Days credited each month on payroll cycle close.'}
                </p>
            </div>

            {/* Monthly Accrual (shown only for MONTHLY) */}
            {policy.accrualType === 'MONTHLY' && (
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                        Monthly Accrual <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputWrapper}>
                        <input
                            type="number"
                            className={`${styles.textInput} ${errors[`accrualPerMonth_${index}`] ? styles.inputError : ''}`}
                            value={policy.accrualPerMonth}
                            onChange={(e) => onUpdate(index, 'accrualPerMonth', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.1"
                        />
                        <span className={styles.inputUnit}>days</span>
                    </div>
                    {errors[`accrualPerMonth_${index}`] && (
                        <p className={styles.fieldError}>{errors[`accrualPerMonth_${index}`]}</p>
                    )}
                    <p className={styles.fieldHint}>
                        Typically: PL = 1.25 days/month (15 days/year), SL = 0.5 days/month (6 days/year).
                    </p>
                </div>
            )}

            {/* Carry Forward */}
            <div className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                    <span className={styles.fieldLabel}>Allow Carry Forward</span>
                    <p className={styles.fieldHint}>
                        Permit unused balance to roll over to next year (up to maximum cap).
                    </p>
                </div>
                <button
                    className={styles.toggleBtn}
                    onClick={() => onUpdate(index, 'carryForwardAllowed', !policy.carryForwardAllowed)}
                    type="button"
                    title={policy.carryForwardAllowed ? 'Enabled' : 'Disabled'}
                >
                    {policy.carryForwardAllowed ? (
                        <span className={styles.toggleOn}>ON</span>
                    ) : (
                        <span className={styles.toggleOff}>OFF</span>
                    )}
                </button>
            </div>

            {/* Max Carry Forward Days (shown when carry forward = ON) */}
            {policy.carryForwardAllowed && (
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Max Carry Forward Days</label>
                    <div className={styles.inputWrapper}>
                        <input
                            type="number"
                            className={`${styles.textInput} ${errors[`maxCarryForward_${index}`] ? styles.inputError : ''}`}
                            value={policy.maxCarryForwardDays}
                            onChange={(e) => onUpdate(index, 'maxCarryForwardDays', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="1"
                        />
                        <span className={styles.inputUnit}>days</span>
                    </div>
                    {errors[`maxCarryForward_${index}`] && (
                        <p className={styles.fieldError}>{errors[`maxCarryForward_${index}`]}</p>
                    )}
                    <p className={styles.fieldHint}>Unused balance exceeding this limit will lapse.</p>
                </div>
            )}

            {/* Encashment */}
            <div className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                    <span className={styles.fieldLabel}>Allow Encashment</span>
                    <p className={styles.fieldHint}>
                        Permit staff to convert unused days into cash (e.g., at exit or year-end).
                    </p>
                </div>
                <button
                    className={styles.toggleBtn}
                    onClick={() => onUpdate(index, 'encashmentAllowed', !policy.encashmentAllowed)}
                    type="button"
                    title={policy.encashmentAllowed ? 'Enabled' : 'Disabled'}
                >
                    {policy.encashmentAllowed ? (
                        <span className={styles.toggleOn}>ON</span>
                    ) : (
                        <span className={styles.toggleOff}>OFF</span>
                    )}
                </button>
            </div>

            {/* Max Encashment Days (shown when encashment = ON) */}
            {policy.encashmentAllowed && (
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Max Encashment Days / Year</label>
                    <div className={styles.inputWrapper}>
                        <input
                            type="number"
                            className={`${styles.textInput} ${errors[`maxEncashment_${index}`] ? styles.inputError : ''}`}
                            value={policy.maxEncashmentDaysPerYear}
                            onChange={(e) => onUpdate(index, 'maxEncashmentDaysPerYear', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="1"
                        />
                        <span className={styles.inputUnit}>days</span>
                    </div>
                    {errors[`maxEncashment_${index}`] && (
                        <p className={styles.fieldError}>{errors[`maxEncashment_${index}`]}</p>
                    )}
                    <p className={styles.fieldHint}>Encashment can be limited to a certain number of days per year.</p>
                </div>
            )}
        </div>
    )
}

export default LeavePolicySetupScreen